import { Box, Button } from '@hover/blueprint';
import { useMediaQuery } from '@hover/blueprint/chakra';
import { iX, iconChevronRight, iconShare } from '@hover/icons';
import { useMachine } from '@xstate/react';
import React, { useCallback, useEffect } from 'react';
import { animated, useTransition } from 'react-spring';

import { FloatingShareCard } from 'src/modules/designStudio/components/DesignMenu/ShareCard/components/FloatingShareCard';
import { CancelButton } from 'src/modules/designStudio/components/DesignMenu/ShareCard/components/common';
import { SlideSmartMenu } from 'src/modules/designStudio/components/Toolbar/SlideSmartMenu';
import type { Menu } from 'src/modules/designStudio/constants';
import { animationConfig } from 'src/modules/designStudio/helpers/animationConfig';
import type { ExtendedMacheteViewer } from 'src/modules/designStudio/helpers/machete';
import { createMachine } from 'src/modules/designStudio/machines/share';
import { ToastType } from 'src/shared/components/Toast/types';
import { useEventListener, useTranslate } from 'src/shared/hooks';
import { useReactNativeWebView } from 'src/shared/hooks/useReactNativeWebView';
import { TranslationNamespace } from 'src/shared/hooks/useTranslate';
import { useToaster } from 'src/shared/providers/Toaster';

import { FastLoader } from '../../FastLoader';
import { CardHeader } from '../CardHeader';

export interface PropTypes {
  closeMenu: () => void;
  headerText: string;
  viewer: ExtendedMacheteViewer | null;
  jobId: number;
  userId?: number;
  menu: Menu | null;
  toolbarRef: React.RefObject<HTMLElement> | null;
}

const ShareCard = ({
  closeMenu,
  jobId,
  headerText,
  userId,
  viewer,
  menu,
  toolbarRef,
}: PropTypes) => {
  const t = useTranslate(TranslationNamespace.DESIGN_STUDIO);
  const { addToast, removeToast } = useToaster();
  const [isWebView] = useReactNativeWebView();
  const [isMobileOrTablet] = useMediaQuery('(max-width: 834px)');

  const [machine, send] = useMachine(() =>
    createMachine({
      jobId,
      userId,
      shareLink: undefined,
      shareToken: undefined,
      viewer,
      addToast,
      removeToast,
      closeMenu,
      translator: t,
      loading: false,
    }),
  );

  useEffect(() => {
    send('CREATE_SHARE');
  }, [send]);

  const handleEvent = useCallback(() => {
    // adds a toast notifying user share has been successful
    addToast({
      title: t('share.shareSuccessful'),
      subtitle: t('share.onePersonInvited'),
      icon: iconShare,
      onPress: removeToast,
      type: ToastType.ACTIVE,
    });
    // close menu when receiving event from RN
    closeMenu();
  }, [addToast, removeToast, closeMenu, t]);

  // Add a listener to handle callback from RN app
  useEventListener('share-finished', handleEvent);

  const disabled = !machine.context.shareLink;
  const { loading } = machine.context;

  const loadingTransition = useTransition(
    loading,
    animationConfig.fadeInAnimation,
  );

  if (!isMobileOrTablet) {
    return (
      <FloatingShareCard
        closeMenu={closeMenu}
        disabled={disabled}
        headerText={headerText}
        send={send}
      />
    );
  }

  return (
    <SlideSmartMenu
      containerRef={toolbarRef}
      disabled
      menu={menu}
      paddingBottom={0}
      paddingLeft={0}
      paddingRight={0}
      testId="slide-up-card"
    >
      <CardHeader
        headerText={headerText}
        marginBottom={0}
        padding={400}
        rightActionHandler={closeMenu}
        rightIcon={iX}
      />

      <Box
        alignItems="center"
        flex={1}
        flexDirection="column"
        justifyContent="space-between"
        width="100%"
      >
        <Box alignItems="center" flex={1} flexDirection="column">
          {loadingTransition((styles, isLoading) =>
            isLoading ? (
              <animated.div style={styles}>
                <FastLoader />
              </animated.div>
            ) : (
              <animated.div
                style={{ position: 'absolute', width: '100%', ...styles }}
              >
                <Box flexDirection="column" padding={400} width="100%">
                  {isWebView && (
                    <Button
                      aria-label="share-to"
                      disabled={disabled}
                      fill="minimal"
                      flex={1}
                      iconAfter={iconChevronRight}
                      label="Share To"
                      onClick={() => send('SHARE_TO_SHARE_SHEET')}
                    >
                      {t('share.shareTo')}
                    </Button>
                  )}
                  <Button
                    aria-label="copy-link"
                    disabled={disabled}
                    iconAfter={iconChevronRight}
                    label="Copy Link"
                    onClick={() => send('COPY_LINK')}
                    variant="secondary"
                  >
                    {t('share.copyLink')}
                  </Button>
                </Box>
              </animated.div>
            ),
          )}
        </Box>
        <CancelButton
          aria-label="cancel-share"
          fill="minimal"
          height="80px"
          label="Cancel"
          onClick={closeMenu}
          testId="cancel-share"
        >
          {t('share.cancel')}
        </CancelButton>
      </Box>
    </SlideSmartMenu>
  );
};

export { ShareCard };
